/**
 * The client/server wire contract. JSON, floats rounded to 2 decimal places.
 *
 * The server is a pure relay: it never simulates, never corrects, and never
 * arbitrates. Ghosts do not collide, so a cheater only cheats themselves.
 *
 * Pure data + guards — no three.js, no DOM.
 */

/** Bumped whenever the message shapes change. A mismatch is rejected at join. */
export const PROTOCOL_VERSION = 1;

/**
 * Which animation the skeleton is playing. Sent as a number to keep snapshots small.
 *
 * - `0` bounce — on or near the ground, cycling
 * - `1` charging — crouched, holding a big jump
 * - `2` airborne — normal flight
 * - `3` flail — falling fast enough to panic
 */
export type AnimState = 0 | 1 | 2 | 3;

export const ANIM_BOUNCE = 0 satisfies AnimState;
export const ANIM_CHARGING = 1 satisfies AnimState;
export const ANIM_AIRBORNE = 2 satisfies AnimState;
export const ANIM_FLAIL = 3 satisfies AnimState;

/** Everything needed to draw one skeleton, per snapshot. Keys are terse on purpose. */
export interface PlayerState {
  /** Position `[x, y, z]`. */
  p: [number, number, number];
  /** Lean `[leanX, leanZ]`, each in `[-1, 1]`. */
  l: [number, number];
  /** Yaw in radians. */
  y: number;
  /** Animation state. */
  a: AnimState;
  /** Charge level in `[0, 1]`. */
  c: number;
  /** Vertical velocity in m/s — drives squash/stretch and the flail threshold. */
  v: number;
}

/** Identity of a player. Sent once on join, never in snapshots. */
export interface PlayerInfo {
  id: number;
  name: string;
  /** Assigned hue as `0xRRGGBB`, always a value from `PALETTE`. */
  color: number;
}

// ---------------------------------------------------------------------------
// Client → server
// ---------------------------------------------------------------------------

/** First message on every connection. `v` must equal `PROTOCOL_VERSION`. */
export interface C2SJoin {
  t: 'join';
  v: number;
  name: string;
  /** Preferred palette colour; honoured only if it is a palette entry and free. */
  color?: number;
}

/** A state update. Rate-limited client-side to `CLIENT_SEND_HZ`. */
export interface C2SState {
  t: 'state';
  s: PlayerState;
}

/** Reply to `S2CPing`, echoing its timestamp. */
export interface C2SPong {
  t: 'pong';
  ts: number;
}

export type C2S = C2SJoin | C2SState | C2SPong;

// ---------------------------------------------------------------------------
// Server → client
// ---------------------------------------------------------------------------

/** Sent once, immediately after a successful join. */
export interface S2CWelcome {
  t: 'welcome';
  /** Your player id. */
  id: number;
  /** Your identity, after name sanitisation and colour assignment. */
  you: PlayerInfo;
  /** Everyone already in the room, with their last known state. */
  players: Array<PlayerInfo & { s: PlayerState }>;
  /** Server broadcast rate, so the client can size its interpolation buffer. */
  hz: number;
}

/** Somebody else joined. */
export interface S2CJoined {
  t: 'joined';
  player: PlayerInfo;
}

/** Somebody else left — drop their ghost. */
export interface S2CLeft {
  t: 'left';
  id: number;
}

/** A snapshot. Contains only players whose state changed since the last tick. */
export interface S2CSnap {
  t: 'snap';
  /** Server send time in ms. Advisory only — clients key their buffer off local receive time. */
  ts: number;
  u: Array<{ id: number } & PlayerState>;
}

/** Heartbeat. The client must answer with `pong`. */
export interface S2CPing {
  t: 'ping';
  ts: number;
}

/** Terminal error; the socket is closed right after. */
export interface S2CError {
  t: 'error';
  code: 'version' | 'full' | 'rate';
  msg: string;
}

export type S2C = S2CWelcome | S2CJoined | S2CLeft | S2CSnap | S2CPing | S2CError;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Round every float in a state to 2 dp. Roughly halves snapshot size and makes
 * client-side change detection stable (identical states serialise identically).
 * Returns a new object; the input is not mutated.
 */
export function roundState(s: PlayerState): PlayerState {
  return {
    p: [r2(s.p[0]), r2(s.p[1]), r2(s.p[2])],
    l: [r2(s.l[0]), r2(s.l[1])],
    y: r2(s.y),
    a: s.a,
    c: r2(s.c),
    v: r2(s.v),
  };
}

function isFiniteNum(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * Structural read of a state object. Rejects the wrong *shape* (missing keys,
 * wrong arity, non-finite numbers) but does not police ranges — that is
 * `clampState` in `src/server/validate.ts`, which clamps rather than kicks.
 *
 * The one exception is `a`: it is a discriminant, not a range, so it is snapped
 * into `0..3` here to keep the returned value type-honest.
 */
function readPlayerState(v: unknown): PlayerState | null {
  if (typeof v !== 'object' || v === null) return null;
  const s = v as Record<string, unknown>;
  const p = s['p'];
  const l = s['l'];
  if (!Array.isArray(p) || p.length !== 3) return null;
  if (!Array.isArray(l) || l.length !== 2) return null;
  if (!isFiniteNum(p[0]) || !isFiniteNum(p[1]) || !isFiniteNum(p[2])) return null;
  if (!isFiniteNum(l[0]) || !isFiniteNum(l[1])) return null;
  const y = s['y'];
  const c = s['c'];
  const vv = s['v'];
  const a = s['a'];
  if (!isFiniteNum(y) || !isFiniteNum(c) || !isFiniteNum(vv) || !isFiniteNum(a)) return null;
  const anim = Math.round(a);
  return {
    p: [p[0], p[1], p[2]],
    l: [l[0], l[1]],
    y,
    a: (anim < 0 ? 0 : anim > 3 ? 3 : anim) as AnimState,
    c,
    v: vv,
  };
}

/**
 * Parse and shape-check one client→server frame.
 *
 * Deliberately lightweight: it proves the message *is* a `C2S` (so the server can
 * switch on `t` without casts) but does no range clamping — that is
 * `clampState` in `src/server/validate.ts`. Never throws; returns `null` on any
 * garbage, which the caller treats as "ignore this frame".
 */
export function parseC2S(raw: string): C2S | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
  const m = parsed as Record<string, unknown>;

  switch (m['t']) {
    case 'join': {
      const v = m['v'];
      const name = m['name'];
      if (!isFiniteNum(v) || typeof name !== 'string') return null;
      const color = m['color'];
      return isFiniteNum(color)
        ? { t: 'join', v, name, color }
        : { t: 'join', v, name };
    }
    case 'state': {
      const s = readPlayerState(m['s']);
      if (s === null) return null;
      return { t: 'state', s };
    }
    case 'pong': {
      const ts = m['ts'];
      if (!isFiniteNum(ts)) return null;
      return { t: 'pong', ts };
    }
    default:
      return null;
  }
}
